import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GridBackground } from '@/components/GridBackground';
import { MainNavigation } from '@/components/MainNavigation';
import { Footer } from '@/components/Footer';

const reportSteps = [
  'Take a screenshot of the suspicious website',
  'Note the URL of the cloned site',
  'Document any suspicious activity or requests',
  'Send all information to security@xtreamcube.com',
  'Include "SECURITY ALERT" in your email subject'
];

const verifiedDomains = [
  'xtreamcube.com',
  'xtreamcube.shop',
  'xtreamcube.xyz',
  'xtreamcube.online',
  'xtreamgate.com'
];

export const ReportPage = () => {
  return (
    <div className="min-h-screen relative">
      <GridBackground />
      <MainNavigation />

      <main className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Button asChild variant="outline" className="mb-8 glass border-primary/30">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12 space-y-8"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <div>
                <h1 className="text-4xl font-bold mb-2">Report Cloned Site</h1>
                <p className="text-muted-foreground">
                  Help us protect the community by reporting suspicious websites claiming to be XtreamCube.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-semibold mb-4">How to Report a Clone</h2>
                <ol className="space-y-3">
                  {reportSteps.map((step, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                        {index + 1}
                      </span>
                      <span className="text-muted-foreground">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="pt-6 border-t border-border">
                <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-primary" />
                  Official Verified Domains
                </h2>
                <p className="text-muted-foreground mb-4">
                  Only the following domains are official XtreamCube properties:
                </p>
                <div className="flex flex-wrap gap-2">
                  {verifiedDomains.map((domain) => (
                    <span
                      key={domain}
                      className="px-4 py-2 rounded-lg bg-primary/10 text-primary text-sm font-medium border border-primary/20"
                    >
                      {domain}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-6">
                <p className="text-sm font-semibold mb-2">⚠️ Warning Signs of Cloned Sites:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Domain names with slight misspellings or extra characters</li>
                  <li>• Requests for sensitive information via email or chat</li>
                  <li>• Missing or invalid SSL certificates (no padlock icon)</li>
                  <li>• Poor website design or broken functionality</li>
                  <li>• Suspicious payment methods or unusual pricing</li>
                </ul>
              </div>

              <div className="text-center pt-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-primary hover:bg-primary/90"
                >
                  <a href="mailto:security@xtreamcube.com?subject=SECURITY ALERT - Cloned Site Report">
                    Report Now via Email
                  </a>
                </Button>
              </div>


            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};